import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Booking,
  BookingLineItem,
  CalculatePriceApplicationErrors,
  CalculatePriceResponse,
  PreviewPriceApplicationErrors,
  PreviewPriceResponse,
  calculatePrice as universalCalculatePrice,
  previewPrice as universalPreviewPrice,
} from './bookings-v2-price-info-pricing.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

export function previewPrice(httpClient: HttpClient): PreviewPriceSignature {
  return (bookingLineItems: BookingLineItem[]) =>
    universalPreviewPrice(
      bookingLineItems,
      // @ts-ignore
      { httpClient }
    );
}

interface PreviewPriceSignature {
  /**
   * Previews the base price for a set of line items belonging to the same service, before a booking is created.
   *
   * The response includes each line item's individual preview price and the total of all line item preview prices.
   * In most situations, the preview price matches the final booking price when using the same parameters.
   * The final amount may differ due to discounts, booking fees, payment processing charges, or service price changes made by the business between preview and booking creation.
   *
   * Preview Price fails if:
   *
   * - You specify line items that belong to different [services](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction).
   * - The site is using a [Bookings Pricing Integration Service Plugin](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-integration-service-plugin/introduction). Note that external apps can't currently check whether a plugin is installed on the site before calling Preview Price.
   *
   * To retrieve the price of an existing booking, call [Calculate Price](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-api/calculate-price) instead.
   * @param - Booking line items to preview pricing for.
   * All line items must belong to the same [service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction).
   */
  (bookingLineItems: BookingLineItem[]): Promise<
    NonNullablePaths<
      PreviewPriceResponse,
      | `priceInfo.calculatedPrice`
      | `priceInfo.priceDescription`
      | `priceInfo.priceDescriptionInfo.original`
      | `priceInfo.bookingLineItems`,
      4
    > & {
      __applicationErrorsType?: PreviewPriceApplicationErrors;
    }
  >;
}

export function calculatePrice(
  httpClient: HttpClient
): CalculatePriceSignature {
  return (booking: Booking) =>
    universalCalculatePrice(
      booking,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculatePriceSignature {
  /**
   * Calculates the current base price for an existing booking.
   *
   *
   * Use this method to display booking costs in external apps or dashboards, or to retrieve accurate pricing for bookings created through other channels.
   *
   * The returned price represents the fundamental booking cost before additional charges during [eCommerce checkout](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction). For recently created unpaid bookings, this typically matches the original booking price. However, this method calculates prices based on the service's current pricing configuration, so the returned price may differ from the original booking price if service pricing has changed since the booking was created.
   *
   * ### Price calculation methods
   *
   * __Standard pricing__: Wix Bookings calculates prices based on the number of participants for each [service variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).
   * Specify `booking.bookedEntity.slot.serviceId` for appointment-based services and classes, or `booking.bookedEntity.schedule.serviceId` for courses.
   *
   * __Custom pricing__: When a [Pricing Service Provider Plugin](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-integration-service-plugin/introduction) is installed, Calculate Price gets responses directly from the external provider instead of using standard Wix pricing logic.
   * In this case, specify `booking.bookedEntity`. Note that external apps can't currently check whether a plugin is installed on the site before calling Calculate Price.
   *
   * To estimate the price for multiple booking line items before the booking exists, call [Preview Price](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-api/preview-price) instead.
   * @param - Existing [booking](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings/bookings-writer-v2/introduction) to calculate the base price for.
   * Must include `bookedEntity` details and participant information.
   */
  (booking: Booking): Promise<
    NonNullablePaths<
      CalculatePriceResponse,
      | `priceInfo.calculatedPrice`
      | `priceInfo.priceDescription`
      | `priceInfo.priceDescriptionInfo.original`
      | `priceInfo.bookingLineItems`,
      4
    > & {
      __applicationErrorsType?: CalculatePriceApplicationErrors;
    }
  >;
}

export {
  Actor,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  BookedAddOn,
  BookedEntity,
  BookedEntityItemOneOf,
  BookedResource,
  BookedSchedule,
  BookedSlot,
  Booking,
  BookingLineItem,
  BookingParticipantsInfoOneOf,
  BookingSource,
  BookingStatus,
  CalculatePriceRequest,
  CalculatePriceResponse,
  ContactDetails,
  CustomFormField,
  Duration,
  ExtendedFields,
  FlowControlSettings,
  IdentificationData,
  IdentificationDataIdOneOf,
  IdentityType,
  Location,
  LocationType,
  MultiServiceBookingInfo,
  MultiServiceBookingType,
  ParticipantChoices,
  ParticipantNotification,
  PaymentStatus,
  PhoneCall,
  Platform,
  PreviewPriceRequest,
  PreviewPriceResponse,
  PriceDescriptionInfo,
  PriceInfo,
  PriceInfoTotalPriceOneOf,
  PricingServiceChoices,
  ResourceSelection,
  ResourceTransparency,
  SelectedPaymentOption,
  SelectionMethod,
  ServiceChoice,
  ServiceChoiceChoiceOneOf,
  ServiceChoices,
  StreetAddress,
  Subdivision,
  ValueType,
} from './bookings-v2-price-info-pricing.universal.js';
